/**
 * Formatting utilities for currency, dates, and numbers
 * Supports Slovak (EUR) and English (USD) locales
 */

export type SupportedLocale = 'sk' | 'en';
export type SupportedCurrency = 'EUR' | 'USD';

/**
 * Format currency according to locale
 * SK: 9,90 € (comma decimal, NBSP, symbol after)
 * EN-US: $14.90 (dot decimal, symbol before)
 */
export function formatCurrency(
  locale: SupportedLocale,
  amount: number,
  currency: SupportedCurrency
): string {
  const localeCode = locale === 'sk' ? 'sk-SK' : 'en-US';
  
  const formatter = new Intl.NumberFormat(localeCode, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let formatted = formatter.format(amount);
  
  // For Slovak locale, ensure proper formatting with NBSP
  if (locale === 'sk' && currency === 'EUR') {
    // Replace regular space with non-breaking space before €
    formatted = formatted.replace(' €', '\u00A0€');
  }
  
  return formatted;
}

/**
 * Format date according to locale
 * SK: DD.MM.YYYY (15.01.2025)
 * EN-US: MM/DD/YYYY (1/15/2025)
 */
export function formatDate(locale: SupportedLocale, date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (locale === 'sk') {
    return dateObj.toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } else {
    return dateObj.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  }
}

/**
 * Format date with time according to locale
 */
export function formatDateTime(locale: SupportedLocale, date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const localeCode = locale === 'sk' ? 'sk-SK' : 'en-US';
  
  return dateObj.toLocaleString(localeCode, {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format number according to locale
 * SK: 1 234,56 (space thousands separator, comma decimal)
 * EN-US: 1,234.56 (comma thousands separator, dot decimal)
 */
export function formatNumber(locale: SupportedLocale, number: number, decimals: number = 2): string {
  const localeCode = locale === 'sk' ? 'sk-SK' : 'en-US';
  
  return new Intl.NumberFormat(localeCode, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
}

/**
 * Parse number from localized string
 * Handles both SK (comma decimal) and EN (dot decimal) formats
 */
export function parseLocalizedNumber(locale: SupportedLocale, value: string): number {
  if (locale === 'sk') {
    // Replace spaces and commas for Slovak format
    const cleaned = value.replace(/\s/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  } else {
    // Remove commas (thousands separator) for EN format
    const cleaned = value.replace(/,/g, '');
    return parseFloat(cleaned) || 0;
  }
}

/**
 * Format percentage according to locale
 */
export function formatPercentage(locale: SupportedLocale, value: number): string {
  const localeCode = locale === 'sk' ? 'sk-SK' : 'en-US';
  
  return new Intl.NumberFormat(localeCode, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
}

/**
 * Helper to get locale and currency from region
 */
export function getLocaleFromRegion(region?: string): { locale: SupportedLocale; currency: SupportedCurrency } {
  if (region === 'US') {
    return { locale: 'en', currency: 'USD' };
  }
  return { locale: 'sk', currency: 'EUR' };
}

/**
 * Format currency based on region (convenience function)
 */
export function formatCurrencyByRegion(region: string | undefined, amount: number): string {
  const { locale, currency } = getLocaleFromRegion(region);
  return formatCurrency(locale, amount, currency);
}

/**
 * Format date based on region (convenience function)
 */
export function formatDateByRegion(region: string | undefined, date: Date | string): string {
  const { locale } = getLocaleFromRegion(region);
  return formatDate(locale, date);
}